import { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client';
import { FadeIn } from '../components/AnimatedPanel';
import type { KorvexGameDetail } from '../types';
import { useAuth } from '../context/AuthContext';

export function GameManagePanel() {
  const { id } = useParams();
  const { user } = useAuth();
  const [game, setGame] = useState<KorvexGameDetail | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(true);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    void api
      .getGame(id)
      .then(({ game: g }) => {
        setGame(g);
        setName(g.name);
        setDescription(g.description);
        setIsPrivate(g.isPrivate);
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (!user) return <div className="panel-card text-center text-korvex-muted">Sign in to manage games.</div>;
  if (loading) return <p className="text-korvex-muted">Loading…</p>;
  if (!game || !game.canEdit) return <p className="text-red-400">Game not found or access denied.</p>;

  async function save() {
    setMsg('');
    try {
      const { game: updated } = await api.updateGame(game!.id, { name, description, isPrivate });
      setGame(updated);
      setMsg('Saved.');
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Save failed.');
    }
  }

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <FadeIn>
        <h2 className="font-display text-3xl font-bold">Manage game</h2>
        <p className="text-korvex-muted">Update settings after publishing from Studio.</p>
      </FadeIn>
      <div className="panel-card space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium">Name</label>
          <input className="input-field" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Description</label>
          <textarea className="input-field min-h-[100px]" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <label className="flex items-center gap-3 text-sm">
          <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} />
          Private (only you can play)
        </label>
        <button type="button" className="btn-primary" onClick={() => void save()}>
          <Save className="h-4 w-4" />
          Save changes
        </button>
        {msg && <p className="text-sm text-korvex-accent">{msg}</p>}
      </div>
    </div>
  );
}
